import { Link, usePage } from "@inertiajs/react";
import type { SharedProps } from "@/types";
import { ticketIcon } from "./NavItems";
import Icon from "@/components/ui/Icon";
import Avatar from "@/components/ui/Avatar";

// The strongest true thing about someone, shown under their name. The ticket
// balance used to sit here, which reads as "0" for everyone now that the economy
// is off.
function roleLabel(user: SharedProps["user"]) {
  if (user.is_admin) return "HQ";
  if (user.is_organizer) return "Organizer";
  if (user.is_reviewer) return "Reviewer";
  return "Attendee";
}

export default function UserCard() {
  const { props } = usePage<SharedProps>();
  const user = props.user;
  const unread = user.unread_notification_count ?? 0;
  const economyOn =
    props.program.shop_enabled || props.program.projects_enabled;

  return (
    <div className="border-line bg-goldenrod text-ink shadow-card border-2 p-3">
      <div className="flex items-center gap-3">
        <Avatar src={user.avatar} name={user.display_name} seed={user.id} />

        <div className="min-w-0 flex-1">
          <div className="font-display text-subheading truncate leading-tight font-bold">
            {user.display_name || "Guest"}
          </div>

          {economyOn ? (
            <div className="flex items-center gap-1.5">
              <Icon src={ticketIcon} className="h-4 w-4" />
              <span className="font-display text-body font-bold">
                {user.balance}
              </span>
            </div>
          ) : (
            <div className="text-caption text-ink/70 font-bold tracking-wide uppercase">
              {roleLabel(user)}
            </div>
          )}
        </div>
      </div>

      <div className="border-line/20 mt-3 flex items-center justify-between border-t-2 pt-2">
        <Link
          href="/notifications"
          title="Notifications"
          className="duration-fast ease-standard relative flex h-8 w-8 items-center justify-center transition-transform hover:scale-110"
        >
          <Icon
            src="/icons/notification-black.svg"
            label="Notifications"
            className={`h-5 w-5 ${unread > 0 ? "text-sunset-orange" : "text-ink"}`}
          />
          {/* A count, not just a colour shift -- "3 waiting" is actionable in a
              way that a slightly different icon isn't. */}
          {unread > 0 && (
            <span className="border-line bg-sunset-orange text-caption absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full border px-1 font-bold text-white">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </Link>

        <Link
          href="/me"
          title="Settings"
          className="duration-fast ease-standard flex h-8 w-8 items-center justify-center transition-transform hover:scale-110"
        >
          <Icon
            src="/icons/settings.svg"
            label="Settings"
            className="text-ink h-5 w-5"
          />
        </Link>

        <Link
          href="/auth/logout"
          method="post"
          as="button"
          title="Log out"
          className="duration-fast ease-standard flex h-8 w-8 cursor-pointer items-center justify-center transition-transform hover:scale-110"
        >
          <Icon
            src="/icons/logout.svg"
            label="Log out"
            className="text-ink h-5 w-5"
          />
        </Link>
      </div>
    </div>
  );
}
